/**
 * Payment Flow Manager for Orello Agreement Payments
 * Handles sequential payments for tenants + single payment for landlords
 * Routes to CURRENT ACCOUNT (fees) or ESCROW ACCOUNT (deposits)
 */

import client from '../api/client';
import { depositPlatformFeePaise, depositFeeGstPaise } from './fees';

// Calculate payment amounts based on agreement details
export function calculatePaymentBreakdown(agreement, userRole, depositAmount, depositProtectionSelected, feePayer) {
  const agreementType = agreement?.agreement_type || 'e_agreement';
  const agreementFee = agreementType === 'e_agreement' ? 9900 : 29900; // in paise

  // Deposit platform fee, v1.0 marginal slices, ex-GST (see lib/fees.js)
  const depositProtectionFee = depositPlatformFeePaise((Number(depositAmount) || 0) * 100);

  const breakdown = {
    userRole,
    agreementType,
    payments: [],
    totalAmount: 0,
  };

  if (userRole === 'landlord') {
    // Landlord pays based on fee_payer setting
    if (feePayer === 'landlord' || feePayer === 'split') {
      const landlordAgreesFee = feePayer === 'landlord' ? agreementFee : Math.floor(agreementFee / 2);
      breakdown.payments.push({
        id: 'agreement_fee',
        type: 'agreement_fee',
        name: `Agreement Fee (${agreementType === 'e_agreement' ? 'E-Agreement' : 'E-Stamp'})`,
        amount: landlordAgreesFee,
        account: 'ORELLO_CURRENT',
        description: 'Digital agreement processing fee',
      });
      breakdown.totalAmount = landlordAgreesFee;
    }
  } else if (userRole === 'tenant') {
    // Tenant pays in sequence: agreement fee → deposit protection fee → deposit

    // 1. Agreement fee
    if (feePayer === 'tenant' || feePayer === 'split') {
      const tenantAgreesFee = feePayer === 'tenant' ? agreementFee : Math.floor(agreementFee / 2);
      breakdown.payments.push({
        id: 'agreement_fee',
        type: 'agreement_fee',
        step: 1,
        name: `Agreement Fee (${agreementType === 'e_agreement' ? 'E-Agreement' : 'E-Stamp'})`,
        amount: tenantAgreesFee,
        account: 'ORELLO_CURRENT',
        description: 'Digital agreement processing fee',
      });
      breakdown.totalAmount += tenantAgreesFee;
    }

    // 2. Deposit platform fee + 18% GST (GST is a separate line, same charge)
    if (depositProtectionSelected && depositProtectionFee > 0) {
      const gst = depositFeeGstPaise(depositProtectionFee);
      breakdown.payments.push({
        id: 'deposit_protection_fee',
        type: 'deposit_protection_fee',
        step: 2,
        name: 'Deposit Platform Fee',
        amount: depositProtectionFee,
        gstAmount: gst,
        account: 'ORELLO_CURRENT',
        description: 'Tenant protection & dispute resolution service (+18% GST)',
      });
      breakdown.totalAmount += depositProtectionFee + gst;
    }

    // 3. Security deposit
    if (depositAmount && depositAmount > 0) {
      breakdown.payments.push({
        id: 'security_deposit',
        type: 'security_deposit',
        step: 3,
        name: 'Security Deposit',
        amount: parseInt(depositAmount * 100), // Convert to paise
        account: 'ORELLO_ESCROW',
        description: 'Held securely in escrow account until lease end',
      });
      breakdown.totalAmount += parseInt(depositAmount * 100);
    }
  }

  return breakdown;
}

// Create Cashfree payment order
export async function createPaymentOrder(paymentItem, agreementId, rentalId) {
  try {
    const payload = {
      order_type: paymentItem.type,
      amount_paise: paymentItem.amount,
      account_type: paymentItem.account,
      agreement_id: agreementId,
      rental_id: rentalId,
      description: paymentItem.description,
    };

    const { data } = await client.post('/agreements/payments/create-order', payload);

    return {
      success: true,
      orderId: data.order_id,
      paymentUrl: data.payment_url,
      sessionId: data.session_id,
      amount: data.amount,
    };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.detail || 'Failed to create payment order',
    };
  }
}

// Store payment session in sessionStorage for PaymentStatus page
export function storePaymentSession(orderId, rentalId, step, totalSteps, paymentType) {
  sessionStorage.setItem('orello_payment_order', orderId);
  sessionStorage.setItem('orello_payment_rental', rentalId);
  sessionStorage.setItem('orello_payment_step', step);
  sessionStorage.setItem('orello_payment_total_steps', totalSteps);
  sessionStorage.setItem('orello_payment_type', paymentType);
}

// Clear payment session
export function clearPaymentSession() {
  sessionStorage.removeItem('orello_payment_order');
  sessionStorage.removeItem('orello_payment_rental');
  sessionStorage.removeItem('orello_payment_step');
  sessionStorage.removeItem('orello_payment_total_steps');
  sessionStorage.removeItem('orello_payment_type');
}

// Format amount for display
export function formatAmount(paise) {
  return (paise / 100).toLocaleString('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
  });
}
